import { useSchemaForm } from "@/components/form";
import { Container, Stack, Typography, IconButton, Alert, Button, Card, CardContent, Skeleton, Divider, FormControl } from "@mui/material";
import { useNavigate } from "react-router";
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getEmisorLogo, useGetEmisoresQuery } from "@/features/configuracion/emisor/api/get-emisor";
import { useUpdateEmisorMutation } from "@/features/configuracion/emisor/api/update-emisor";
import { z } from "zod/v4";
import { Form } from "@/components/form";
import DepartamentoManualSearch from "@/features/auxiliar/departamentos/components/departamento-manual-search";
import CiudadManualSearch from "@/features/auxiliar/ciudades/components/ciudad-manual-search";
import { Departamento } from "@/shared/types/departamentos";
import { Ciudad } from "@/shared/types/ciudad";
import { Dialog, ModalHeader, ModalContextExtractor, useOpenModal } from "@/components/modal";
import { useEffect, useRef, useState } from "react";
import { useGetAllActividadesComercalesQuery } from "@/features/auxiliar/actividades-comerciales/api/get-all-actividades-comerciales";

const establecimientoSchema = z.object({
    punto: z.string().min(1, "Punto es requerido"),
    email: z.string().email("Email inválido").min(1, "Email es requerido"),
    telefono: z.string().min(1, "Teléfono es requerido"),
    direccion: z.string().min(1, "Dirección es requerida"),
    numCasa: z.union([
        z.number(),
        z.string().trim().regex(/^\d+$/, "Número inválido")
    ])
        .transform(v => typeof v === "string" ? Number(v) : v)
        .refine(n => n >= 0, { message: "Debe ser 0 o mayor" }),
    ciudadCodigo: z.string().min(1, "Código de ciudad es requerido"),
    departamentoCodigo: z.string().min(1, "Código de departamento es requerido"),
    departamentoSeleccionado: z.string().optional(),
    ciudadSeleccionada: z.string().optional(),
});

const emisorFormSchema = z.object({
    nombre: z.string().min(1, "Nombre es requerido"),
    tipoContribuyente: z.enum(["1", "2"]).default("1"),
    numDocumento: z.string().min(1, "Número de documento es requerido"),
    digitoVerificador: z.number().min(0).max(9),
    actividadComercialId: z.string().nonempty("Actividad comercial es requerida"),
    tipoTransaccion: z.number().default(1),
    tipoImpuesto: z.number().default(1),
    timbrado: z.string().regex(/^\d{8}$/, "El timbrado debe tener exactamente 8 dígitos"),
    fechaInicioTimbrado: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "El formato debe ser AAAA-MM-DD"),
    cSC1: z.string().min(1, "CSC1 es requerido"),
    pathCertificado: z.string().min(1, "Ruta del certificado es requerida"),
    passwordCertificado: z.string().min(1, "Contraseña del certificado es requerida"),
    logo: z.instanceof(File).nullable().default(null),
    establecimientos: z.array(establecimientoSchema).min(1, "Se requiere al menos un establecimiento"),
});

type EmisorFormValues = z.infer<typeof emisorFormSchema>;

export default function EmisorPage() {
    const navigate = useNavigate();
    const { data: emisores, isLoading } = useGetEmisoresQuery();
    const { data: actividadesComerciales } = useGetAllActividadesComercalesQuery({});
    const emisor = emisores?.[0]; // Primer emisor (default)
    const updateEmisorMutation = useUpdateEmisorMutation();
    const openModal = useOpenModal();
    const [showPassword, setShowPassword] = useState(false);
    const selectedEstablecimientoIndexRef = useRef<number | null>(null);
    const [formValues, setFormValues] = useState<EmisorFormValues | undefined>(undefined);

    const form = useSchemaForm({
        schema: emisorFormSchema,
        defaultValues: emisor ? {
            nombre: emisor.nombre,
            tipoContribuyente: emisor.tipoContribuyente.toString() as "1" | "2",
            numDocumento: emisor.numDocumento,
            digitoVerificador: emisor.digitoVerificador,
            actividadComercialId: emisor.actividadComercialId || "",
            tipoTransaccion: (emisor.tipoTransaccion ?? 1),
            tipoImpuesto: (emisor.tipoImpuestoAfectado ?? 1),
            timbrado: (emisor.timbrado ?? 0).toString().padStart(8, '0'),
            fechaInicioTimbrado: emisor.fechaInicioTimbrado || "",
            cSC1: emisor.cSC1 || "",
            pathCertificado: emisor.pathCertificado || "",
            passwordCertificado: emisor.passwordCertificado || "",
            logo: null,
            establecimientos: (emisor.establecimientos && emisor.establecimientos.length > 0)
                ? emisor.establecimientos.map(est => ({
                    punto: est.punto || "",
                    email: est.email || "",
                    telefono: est.telefono || "",
                    direccion: est.direccion || "",
                    numCasa: est.numCasa || 0,
                    ciudadCodigo: est.ciudadCodigo || "",
                    departamentoCodigo: est.departamentoCodigo || "",
                    departamentoSeleccionado: est.departamentoNombre || "",
                    ciudadSeleccionada: est.ciudadNombre || "",
                }))
                : [{
                    punto: "",
                    email: "",
                    telefono: "",
                    direccion: "",
                    numCasa: 0,
                    ciudadCodigo: "",
                    departamentoCodigo: "",
                    departamentoSeleccionado: "",
                    ciudadSeleccionada: "",
                }],
        } : undefined,
        onSubmit: async ({ value }) => {
            if (!emisor) return;

            await updateEmisorMutation.mutateAsync({
                id: emisor.id,
                data: {
                    nombre: value.nombre,
                    tipoContribuyente: parseInt(value.tipoContribuyente),
                    numDocumento: value.numDocumento,
                    digitoVerificador: value.digitoVerificador,
                    actividadComercialId: value.actividadComercialId,
                    tipoTransaccion: value.tipoTransaccion,
                    tipoImpuestoAfectado: value.tipoImpuesto,
                    timbrado: value.timbrado,
                    fechaInicioTimbrado: value.fechaInicioTimbrado,
                    cSC1: value.cSC1?.trim(),
                    pathCertificado: value.pathCertificado?.trim(),
                    passwordCertificado: value.passwordCertificado?.trim(),
                    logo: value.logo,
                    establecimientos: value.establecimientos.map(est => ({
                        punto: est.punto,
                        email: est.email,
                        telefono: est.telefono,
                        direccion: est.direccion,
                        numCasa: est.numCasa,
                        ciudadCodigo: est.ciudadCodigo,
                        departamentoCodigo: est.departamentoCodigo,
                    })),
                }
            });
            setFormValues(value);
        },
    });

    // Inicializa los valores del form dinámico para renderizar establecimientos
    useEffect(() => {
        if (emisor) {
            const ests = (emisor.establecimientos && emisor.establecimientos.length > 0)
                ? emisor.establecimientos.map(est => ({
                    punto: est.punto || "",
                    email: est.email || "",
                    telefono: est.telefono || "",
                    direccion: est.direccion || "",
                    numCasa: est.numCasa || 0,
                    ciudadCodigo: est.ciudadCodigo || "",
                    departamentoCodigo: est.departamentoCodigo || "",
                    departamentoSeleccionado: est.departamentoNombre || "",
                    ciudadSeleccionada: est.ciudadNombre || "",
                }))
                : [{
                    punto: "",
                    email: "",
                    telefono: "",
                    direccion: "",
                    numCasa: 0,
                    ciudadCodigo: "",
                    departamentoCodigo: "",
                    departamentoSeleccionado: "",
                    ciudadSeleccionada: "",
                }];
            setFormValues({
                nombre: emisor.nombre,
                tipoContribuyente: emisor.tipoContribuyente.toString() as "1" | "2",
                numDocumento: emisor.numDocumento,
                digitoVerificador: emisor.digitoVerificador,
                actividadComercialId: emisor.actividadComercialId || "",
                tipoTransaccion: (emisor.tipoTransaccion ?? 1),
                tipoImpuesto: (emisor.tipoImpuestoAfectado ?? 1),
                timbrado: (emisor.timbrado ?? 0).toString().padStart(8, '0'),
                fechaInicioTimbrado: emisor.fechaInicioTimbrado || "",
                cSC1: emisor.cSC1 || "",
                pathCertificado: emisor.pathCertificado || "",
                passwordCertificado: emisor.passwordCertificado || "",
                logo: null,
                establecimientos: ests,
            });
        }
    }, [emisor]);

    useEffect(() => {
        if (!emisor?.id || !emisor.nombreLogo) {
            return;
        }

        let isMounted = true;

        const loadLogo = async () => {
            try {
                const logoFile = await getEmisorLogo(emisor.id, emisor.nombreLogo!);
                if (!isMounted) return;
                form.setFieldValue("logo", logoFile);
            } catch {
                if (!isMounted) return;
                form.setFieldValue("logo", null);
            }
        };

        loadLogo();

        return () => {
            isMounted = false;
        };
    }, [emisor?.id, emisor?.nombreLogo]);

    const handleDepartamentoSelect = (departamento: Departamento) => {
        const selectedIndex = selectedEstablecimientoIndexRef.current;

        if (selectedIndex !== null) {
            setFormValues((prev) => {
                if (!prev?.establecimientos) return prev;

                const updated = [...prev.establecimientos];
                updated[selectedIndex] = {
                    ...updated[selectedIndex],
                    departamentoCodigo: departamento.codigo,
                    departamentoSeleccionado: departamento.nombre,
                    ciudadCodigo: "",
                    ciudadSeleccionada: "",
                };

                return { ...prev, establecimientos: updated };
            });

            form.setFieldValue(`establecimientos[${selectedIndex}].departamentoCodigo`, departamento.codigo);
            form.setFieldValue(`establecimientos[${selectedIndex}].departamentoSeleccionado`, departamento.nombre);
            form.setFieldValue(`establecimientos[${selectedIndex}].ciudadCodigo`, "");
            form.setFieldValue(`establecimientos[${selectedIndex}].ciudadSeleccionada`, "");
        }
    };

    const handleCiudadSelect = (ciudad: Ciudad) => {
        const selectedIndex = selectedEstablecimientoIndexRef.current;

        if (selectedIndex !== null) {
            setFormValues((prev) => {
                if (!prev?.establecimientos) return prev;

                const updated = [...prev.establecimientos];
                updated[selectedIndex] = {
                    ...updated[selectedIndex],
                    ciudadCodigo: ciudad.codigo,
                    ciudadSeleccionada: ciudad.nombre,
                };

                return { ...prev, establecimientos: updated };
            });

            form.setFieldValue(`establecimientos[${selectedIndex}].ciudadCodigo`, ciudad.codigo);
            form.setFieldValue(`establecimientos[${selectedIndex}].ciudadSeleccionada`, ciudad.nombre);
        }
    };

    if (isLoading) {
        return (
            <Container flex={1} component={Stack} maxWidth="md" gap={2} paddingY={2}>
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <BusinessIcon color="primary" />
                    <Typography component={"h1"} variant="h5" flex={1}>
                        Datos del Emisor Electronico
                    </Typography>
                </Stack>

                <Card>
                    <CardContent>
                        <Stack gap={3}>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Información Básica
                            </Typography>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                                <Skeleton variant="rectangular" height={40} width={200} />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                                <Skeleton variant="rectangular" height={40} width={150} />
                            </Stack>

                            <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                Establecimientos
                            </Typography>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                                <Skeleton variant="rectangular" height={40} width={150} />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Skeleton variant="rectangular" height={40} sx={{ flex: 1 }} />
                            </Stack>

                            <Stack direction="row" gap={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                                <Skeleton variant="rectangular" width={100} height={36} />
                                <Skeleton variant="rectangular" width={150} height={36} />
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    if (!emisor) {
        return (
            <Container flex={1} component={Stack} maxWidth="xl" gap={2} paddingY={2}>
                <Alert severity="error">
                    No se encontró información del emisor
                </Alert>
            </Container>
        );
    }

    return (
        <Container flex={1} component={Stack} maxWidth="md" gap={2} paddingY={2}>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
                <BusinessIcon color="primary" />
                <Typography component={"h1"} variant="h5" flex={1}>
                    Datos del Emisor Electrónico
                </Typography>
            </Stack>

            <Card>
                <CardContent>
                    <Form form={form} gap={3}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            Información Básica
                        </Typography>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                            <Form.TextField
                                name="nombre"
                                label="Nombre / Razón Social *"
                                size="small"
                                fullWidth
                            />
                            <Form.Select name="tipoContribuyente" label="Tipo de Contribuyente *" size="small" sx={{ minWidth: 200 }}>
                                <Form.Select.Option value="1">Persona Física</Form.Select.Option>
                                <Form.Select.Option value="2">Persona Jurídica</Form.Select.Option>
                            </Form.Select>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                            <Form.TextField
                                name="numDocumento"
                                label="Número de Documento *"
                                size="small"
                                fullWidth
                            />
                            <Form.NumberField
                                name="digitoVerificador"
                                label="Dígito Verificador *"
                                size="small"
                                sx={{ maxWidth: 150 }}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                            <Form.Select
                                name="actividadComercialId"
                                label="Actividad Comercial *"
                                size="small"
                                sx={{ width: '100%' }}
                            >
                                {actividadesComerciales?.result?.map(ac => (
                                    <Form.Select.Option key={ac.id} value={ac.id}>
                                        {ac.codigo} - {ac.nombre}
                                    </Form.Select.Option>
                                ))}
                            </Form.Select>
                        </Stack>

                        <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 1 }}>
                            Establecimientos
                        </Typography>
                        <Form.ErrorText name="establecimientos" />

                        {formValues?.establecimientos?.map((_, index) => (
                            <Stack
                                key={index}
                                gap={2.5}
                                sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" color="primary">
                                        Establecimiento {index + 1}
                                    </Typography>
                                    {formValues?.establecimientos && formValues.establecimientos.length > 1 && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                if (formValues?.establecimientos) {
                                                    const updated = formValues.establecimientos.filter((_, i) => i !== index);
                                                    setFormValues({ ...formValues, establecimientos: updated });
                                                    form.setFieldValue("establecimientos", updated);
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                    <Form.TextField
                                        name={`establecimientos[${index}].punto`}
                                        label="Punto de Establecimiento *"
                                        size="small"
                                        fullWidth
                                        placeholder="Ej: 001, 002, etc."
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                    <Form.TextField
                                        name={`establecimientos[${index}].email`}
                                        label="Email *"
                                        size="small"
                                        fullWidth
                                        type="email"
                                    />
                                    <Form.TextField
                                        name={`establecimientos[${index}].telefono`}
                                        label="Teléfono *"
                                        size="small"
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                    <Form.TextField
                                        name={`establecimientos[${index}].direccion`}
                                        label="Dirección *"
                                        size="small"
                                        fullWidth
                                    />
                                    <Form.NumberField
                                        name={`establecimientos[${index}].numCasa`}
                                        label="Número de Casa *"
                                        size="small"
                                        sx={{ maxWidth: 150 }}
                                        inputProps={{ min: 0 }}
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                    <Stack direction="row" gap={1} alignItems="flex-start" flex={1}>
                                        <FormControl fullWidth>
                                            <Form.TextField
                                                name={`establecimientos[${index}].departamentoSeleccionado`}
                                                label="Departamento *"
                                                size="small"
                                                fullWidth
                                                disabled
                                                placeholder="Selecciona un departamento..."
                                            />
                                            <Form.ErrorText name={`establecimientos[${index}].departamentoCodigo`} sx={{ mt: 0.5 }} />
                                        </FormControl>
                                        <IconButton
                                            onClick={() => {
                                                selectedEstablecimientoIndexRef.current = index;
                                                openModal("searchDepartamento");
                                            }}
                                            color="primary"
                                            size="small"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </Stack>
                                    <Stack direction="row" gap={1} alignItems="flex-start" flex={1}>
                                        <FormControl fullWidth>
                                            <Form.TextField
                                                name={`establecimientos[${index}].ciudadSeleccionada`}
                                                label="Ciudad *"
                                                size="small"
                                                fullWidth
                                                disabled
                                                placeholder="Selecciona una ciudad..."
                                            />
                                            <Form.ErrorText name={`establecimientos[${index}].ciudadCodigo`} sx={{ mt: 0.5 }} />
                                        </FormControl>
                                        <IconButton
                                            onClick={() => {
                                                selectedEstablecimientoIndexRef.current = index;
                                                openModal("searchCiudad");
                                            }}
                                            color="primary"
                                            size="small"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </Stack>
                                </Stack>

                                {index < formValues?.establecimientos?.length! - 1 && (
                                    <Divider sx={{ my: 1 }} />
                                )}
                            </Stack>
                        ))}

                        <Button
                            startIcon={<AddIcon />}
                            variant="outlined"
                            onClick={() => {
                                if (formValues?.establecimientos) {
                                    const next = [
                                        ...formValues.establecimientos,
                                        {
                                            punto: "",
                                            email: "",
                                            telefono: "",
                                            direccion: "",
                                            numCasa: 0,
                                            ciudadCodigo: "",
                                            departamentoCodigo: "",
                                            departamentoSeleccionado: "",
                                            ciudadSeleccionada: "",
                                        }
                                    ];
                                    setFormValues({ ...formValues, establecimientos: next });
                                    form.setFieldValue("establecimientos", next);
                                }
                            }}
                        >
                            Agregar Establecimiento
                        </Button>

                        <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 0 }}>
                            Operación Comercial
                        </Typography>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                            <Stack minWidth="180px" flex={1}>
                                <Form.Select name="tipoTransaccion" label="Tipo de Transacción *" size="small">
                                    <Form.Select.Option value={1}>Venta de mercadería</Form.Select.Option>
                                    <Form.Select.Option value={2}>Prestación de servicios</Form.Select.Option>
                                    <Form.Select.Option value={3}>Mixto (Venta de mercadería y servicios)</Form.Select.Option>
                                    <Form.Select.Option value={4}>Venta de activo fijo</Form.Select.Option>
                                    <Form.Select.Option value={5}>Venta de divisas</Form.Select.Option>
                                    <Form.Select.Option value={6}>Compra de divisas</Form.Select.Option>
                                    <Form.Select.Option value={7}>Promoción o entrega de muestras</Form.Select.Option>
                                    <Form.Select.Option value={8}>Donación</Form.Select.Option>
                                    <Form.Select.Option value={9}>Anticipo</Form.Select.Option>
                                    <Form.Select.Option value={10}>Compra de productos</Form.Select.Option>
                                    <Form.Select.Option value={11}>Compra de servicios</Form.Select.Option>
                                    <Form.Select.Option value={12}>Venta de crédito fiscal</Form.Select.Option>
                                    <Form.Select.Option value={13}>Muestras médicas (Art. 3 RG 24/2014)</Form.Select.Option>
                                </Form.Select>
                            </Stack>
                            <Stack minWidth="180px" flex={1}>
                                <Form.Select name="tipoImpuesto" label="Tipo Impuesto Afectado *" size="small">
                                    <Form.Select.Option value={1}>IVA</Form.Select.Option>
                                    <Form.Select.Option value={2}>ISC</Form.Select.Option>
                                    <Form.Select.Option value={3}>Renta</Form.Select.Option>
                                    <Form.Select.Option value={4}>Ninguno</Form.Select.Option>
                                    <Form.Select.Option value={5}>IVA - Renta</Form.Select.Option>
                                </Form.Select>
                            </Stack>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            <Form.TextField
                                name="timbrado"
                                label="Timbrado *"
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 8 }}
                                helperText="Exactamente 8 dígitos"
                            />
                            <Form.TextField
                                name="fechaInicioTimbrado"
                                label="Inicio Timbrado *"
                                size="small"
                                fullWidth
                                placeholder="AAAA-MM-DD"
                                helperText="Formato: AAAA-MM-DD"
                            />
                        </Stack>

                        <Stack>
                            <Form.TextField
                                name="cSC1"
                                label="CSC1 *"
                                size="small"
                                fullWidth
                                placeholder="Código de Control de Seguridad"
                            />
                        </Stack>

                        <Stack>
                            <Form.TextField
                                name="pathCertificado"
                                label="Ruta del Certificado *"
                                size="small"
                                fullWidth
                                placeholder="Ruta al archivo del certificado digital"
                            />
                        </Stack>

                        <Stack direction="row" gap={1} alignItems="center">
                            <Form.TextField
                                name="passwordCertificado"
                                label="Contraseña del Certificado Digital *"
                                size="small"
                                fullWidth
                                type={showPassword ? "text" : "password"}
                                placeholder="Ingresa la contraseña del certificado"
                            />
                            <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                color="primary"
                                size="small"
                                sx={{ mt: 0 }}
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </Stack>

                        <Typography variant="h6" color="primary" gutterBottom>
                            Logo del Emisor
                            <Typography variant="body2" color="text.secondary">
                                Tamaño recomendado: 200 x 60 px
                            </Typography>
                        </Typography>

                        <Stack gap={1.5}>
                            <Form.ImageField
                                name="logo"
                                minHeight="150px"
                            />
                        </Stack>

                        <Stack direction="row" gap={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(-1)}
                            >
                                Cancelar
                            </Button>
                            <Form.SubmitButton
                                variant="contained"
                                loading={updateEmisorMutation.isPending}
                            >
                                Guardar Cambios
                            </Form.SubmitButton>
                        </Stack>
                    </Form>
                </CardContent>
            </Card>

            <Dialog code="searchDepartamento" size="md">
                <ModalHeader>Seleccionar Departamento</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => <DepartamentoManualSearch
                        padding={3}
                        onDepartamentoSelect={(departamento) => {
                            handleDepartamentoSelect(departamento);
                            handleClose();
                        }}
                    />}
                </ModalContextExtractor>
            </Dialog>

            <Dialog code="searchCiudad" size="md">
                <ModalHeader>Seleccionar Ciudad</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => <CiudadManualSearch
                        padding={3}
                        onCiudadSelect={(ciudad) => {
                            handleCiudadSelect(ciudad);
                            handleClose();
                        }}
                    />}
                </ModalContextExtractor>
            </Dialog>

        </Container>
    );
}
